"use client";

import { useEffect, useMemo, useRef, useState } from "react";

export type SearchableOption = {
  id: string;
  label: string;
  meta?: string;
};

function tokensOf(q: string): string[] {
  return q
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .split(/[^a-z0-9]+/i)
    .filter(Boolean);
}

function matches(label: string, tokens: string[]): boolean {
  if (!tokens.length) return true;
  const hay = label
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  return tokens.every((t) => hay.includes(t));
}

type Props = {
  options: SearchableOption[];
  value: string | null;
  disabled?: boolean;
  placeholder?: string;
  onChange: (id: string | null) => void;
};

export function SearchableSelect({
  options,
  value,
  disabled,
  placeholder = "Rechercher…",
  onChange,
}: Props) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({});
  const selected = options.find((o) => o.id === value) ?? null;

  const placePanel = () => {
    const el = rootRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const width = Math.min(Math.max(r.width, 320), 520);
    const left = Math.min(r.left, window.innerWidth - width - 8);
    setPanelStyle({
      position: "fixed",
      top: Math.min(r.bottom + 2, window.innerHeight - 280),
      left: Math.max(8, left),
      width,
      zIndex: 80,
    });
  };

  const sorted = useMemo(
    () =>
      [...options].sort((a, b) =>
        a.label.localeCompare(b.label, "fr", { sensitivity: "base" }),
      ),
    [options],
  );

  const tokens = useMemo(() => tokensOf(q), [q]);
  const filtered = useMemo(
    () =>
      sorted.filter((o) => matches(`${o.label} ${o.meta ?? ""}`, tokens)).slice(0, 80),
    [sorted, tokens],
  );

  useEffect(() => {
    if (!open) return;
    placePanel();
    setQ("");
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onScroll = () => placePanel();
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onScroll);
    // capture scroll from table container
    document.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onScroll);
      document.removeEventListener("scroll", onScroll, true);
    };
  }, [open]);

  return (
    <div className={`ss${open ? " open" : ""}${disabled ? " disabled" : ""}`} ref={rootRef}>
      <button
        type="button"
        className="ss-trigger"
        disabled={disabled}
        aria-expanded={open}
        onClick={() => !disabled && setOpen((o) => !o)}
      >
        <span className={selected ? "" : "ss-ph"}>
          {selected ? selected.label : "— Choisir —"}
        </span>
        <span className="ss-caret" aria-hidden>
          ▾
        </span>
      </button>

      {open && (
        <div className="ss-panel" role="listbox" style={panelStyle}>
          <input
            className="ss-search"
            type="search"
            autoFocus
            placeholder={placeholder}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
          <div className="ss-list">
            <button
              type="button"
              className="ss-item muted"
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
            >
              — Aucun —
            </button>
            {filtered.map((o) => (
              <button
                key={o.id}
                type="button"
                className={`ss-item${o.id === value ? " on" : ""}`}
                onClick={() => {
                  onChange(o.id);
                  setOpen(false);
                }}
              >
                <span>{o.label}</span>
                {o.meta ? <small>{o.meta}</small> : null}
              </button>
            ))}
            {!filtered.length && (
              <div className="ss-empty muted">Aucun résultat pour « {q} »</div>
            )}
            {tokens.length > 0 && filtered.length === 80 && (
              <div className="ss-empty muted">80 premiers — affine la recherche</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
